import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useKonfipayNotifications } from "./hooks/useKonfipayNotifications";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import AnnouncementBar from "./components/AnnouncementBar";
import LoadingModal from "./components/LoadingModal";
import "./App.css";
import { ScaleProvider, useScale } from "./context/ScaleContext";
import { TerminalProvider, useTerminal } from "./context/TerminalContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { SYRINGES_ACCESS_ROLES } from "./utils/syringesPermissions";
import NotFound from "./pages/NotFound";
import { isPublicPath } from "./auth/publicPaths.js";
import { lazyInPage, lazyPage, pageImport } from "./utils/staleBuild";

const loadNavbar = pageImport(() => import("./components/Navbar"));
const Navbar = React.lazy(loadNavbar);
// Failures surface when Navbar renders; this call only preloads.
loadNavbar().catch(() => {});
const MobileBottomNav = lazyPage(() => import("./components/mobile/MobileBottomNav"));
const MobileHub = lazyPage(() => import("./pages/mobile/MobileHub"));
const ScaleModal = lazyInPage(
  () => import("./components/bestellungen/abwiegen/ScaleModal"),
);
const TerminalModal = lazyInPage(
  () => import("./components/bestellungen/pickup/TerminalModal"),
);

function ScaleModalGate() {
  const { showScaleModal } = useScale();
  if (!showScaleModal) return null;
  return (
    <Suspense fallback={null}>
      <ScaleModal />
    </Suspense>
  );
}

function TerminalModalGate() {
  const { showTerminalModal } = useTerminal();
  if (!showTerminalModal) return null;
  return (
    <Suspense fallback={null}>
      <TerminalModal />
    </Suspense>
  );
}

const Uebersicht = lazyPage(() => import("./pages/uebersicht"));
const Syringes = lazyPage(() => import("./pages/syringes/page"));
const Bestellungen = lazyPage(() => import("./pages/bestellungen"));
const BestellungenDruck = lazyPage(
  () => import("./pages/bestellungen/bestellungen-druck/BestellungenDruck"),
);
const BestellungenTracking = lazyPage(
  () => import("./pages/bestellungen/tracking/BestellungenTracking"),
);
const Kundenmails = lazyPage(
  () => import("./pages/bestellungen/kundenmails/Kundenmails"),
);
const KundenmailsRead = lazyPage(
  () => import("./pages/bestellungen/kundenmails/KundenmailsRead"),
);
const StockPreparation = lazyPage(
  () => import("./pages/products/stockPreparation"),
);
const BranchProductOverview = lazyPage(
  () => import("./pages/products/BranchProductOverview"),
);
const PickupBasket = lazyPage(
  () => import("./pages/bestellungen/pickup/PickupBasket"),
);
const PackageScan = lazyPage(
  () => import("./pages/bestellungen/package-scan/PackageScan"),
);
const Abpacken = lazyPage(
  () => import("./pages/bestellungen/abpacken/Abpacken"),
);
const Einkauf = lazyPage(() => import("./pages/einkauf/Einkauf"));
const Statistik = lazyPage(() => import("./pages/statistik"));
const Artikel = lazyPage(() => import("./pages/products/artikel"));
const Inventur = lazyPage(() => import("./pages/inventur"));
const Reservations = lazyPage(() => import("./pages/reservations"));
const ManuelleZahlung = lazyPage(() => import("./pages/ManuelleZahlung"));
const Administration = lazyPage(() => import("./pages/Administration"));
const SystemLogs = lazyPage(() => import("./pages/SystemLogs"));
const Lieferanten = lazyPage(() => import("./pages/Lieferanten"));
const Packmittel = lazyPage(() => import("./pages/packmittel"));
const Abwiegen = lazyPage(
  () => import("./pages/bestellungen/abwiegen/Abwiegen"),
);
const Payments = lazyPage(() => import("./pages/payments/payments"));
const KonfipayPayments = lazyPage(() => import("./pages/payments/KonfipayPayments"));
const Telemediziner = lazyPage(() => import("./pages/telemediziner"));
const Produktemapping = lazyPage(
  () => import("./pages/products/produktemapping"),
);
const ProtocolsPage = lazyPage(
  () => import("./pages/bestellungen/protocols/Protocols"),
);
const ResetPrint = lazyPage(
  () => import("./pages/administration/reset-print"),
);
const SchedulerAdmin = lazyPage(
  () => import("./pages/administration/SchedulerAdmin"),
);
const MollieTerminalsAdmin = lazyPage(
  () => import("./pages/administration/mollie-terminals"),
);
const WarehouseDashboardAdmin = lazyPage(
  () => import("./pages/administration/WarehouseDashboardAdmin"),
);
const MailKnowledgeBase = lazyPage(
  () => import("./pages/bestellungen/mail-knowledge-base/MailKnowledgeBase"),
);
const TicketCenter = lazyPage(() => import("./pages/ticketcenter/page"));
const MarketplaceStatistics = lazyPage(
  () => import("./pages/finance/statistics-marketplace/page"),
);
const Recipes = lazyPage(() => import("./pages/recipes/Recipes"));
const DevErrorBoundaryTest = lazyPage(
  () => import("./pages/DevErrorBoundaryTest"),
);
const DeliveryDatePage = lazyPage(
  () => import("./pages/public/DeliveryDatePage"),
);
const WarehouseDisplayPage = lazyPage(
  () => import("./pages/public/WarehouseDisplayPage"),
);

function AppContent() {
  const { isAuthenticated, isBootstrapping } = useAuth();
  useKonfipayNotifications();
  const location = useLocation();
  const [announcementOffset, setAnnouncementOffset] = useState(0);

  const isPublicChromelessPage = isPublicPath(location.pathname);

  const showAppChrome =
    !isBootstrapping &&
    isAuthenticated &&
    !isPublicChromelessPage &&
    location.pathname !== "/" &&
    location.pathname !== "/syringes" &&
    location.pathname !== "/abpacken";

  useEffect(() => {
    if (!showAppChrome) {
      setAnnouncementOffset(0);
      document.documentElement.style.setProperty(
        "--app-announcement-height",
        "0px",
      );
    }
  }, [showAppChrome]);

  return (
    <>
      {showAppChrome && (
        <>
          <AnnouncementBar onOffsetChange={setAnnouncementOffset} />
          <Suspense fallback={null}>
            <Navbar topOffset={announcementOffset} />
          </Suspense>
          <Suspense fallback={null}>
            <MobileBottomNav />
          </Suspense>
        </>
      )}
      <div
        className={
          isAuthenticated && !isPublicChromelessPage
            ? `content-wrapper${showAppChrome ? " content-wrapper--with-mobile-nav" : " content-wrapper--flush"}`
            : ""
        }
      >
        <Suspense
          fallback={<LoadingModal isOpen message="Seite wird geladen..." />}
        >
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/lieferdatum/:token" element={<DeliveryDatePage />} />
            <Route path="/display/:token" element={<WarehouseDisplayPage />} />
            <Route
              path="/syringes"
              element={
                <ProtectedRoute roles={SYRINGES_ACCESS_ROLES}>
                  <Syringes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/m/:sectionId"
              element={
                <ProtectedRoute>
                  <MobileHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/uebersicht"
              element={
                <ProtectedRoute>
                  <Uebersicht />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reservations"
              element={
                <ProtectedRoute>
                  <Reservations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bestellungen"
              element={
                <ProtectedRoute>
                  <Bestellungen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bestellungen-druck"
              element={
                <ProtectedRoute>
                  <BestellungenDruck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bestellungen-tracking"
              element={
                <ProtectedRoute>
                  <BestellungenTracking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kundenmails"
              element={
                <ProtectedRoute cap="kundenmails.sehen">
                  <Kundenmails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kundenmails/:mailId"
              element={
                <ProtectedRoute cap="kundenmails.sehen">
                  <KundenmailsRead />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mail-wissensdatenbank"
              element={
                <ProtectedRoute>
                  <MailKnowledgeBase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pickup-basket"
              element={
                <ProtectedRoute>
                  <PickupBasket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/package-scan"
              element={
                <ProtectedRoute>
                  <PackageScan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/abpacken"
              element={
                <ProtectedRoute>
                  <Abpacken />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Artikel />
                </ProtectedRoute>
              }
            />
            <Route path="/wareneingang" element={<Navigate to="/einkauf" replace />} />
            <Route path="/warenpruefung" element={<Navigate to="/einkauf" replace />} />
            <Route path="/buchhaltung" element={<Navigate to="/einkauf" replace />} />
            <Route
              path="/einkauf"
              element={
                <ProtectedRoute cap="einkauf.lesen">
                  <Einkauf />
                </ProtectedRoute>
              }
            />
            <Route
              path="/packmittel"
              element={
                <ProtectedRoute>
                  <Packmittel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventur"
              element={
                <ProtectedRoute>
                  <Inventur />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistik"
              element={
                <ProtectedRoute>
                  <Statistik />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recipes"
              element={
                <ProtectedRoute>
                  <Recipes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manuelle-zahlung"
              element={
                <ProtectedRoute>
                  <ManuelleZahlung />
                </ProtectedRoute>
              }
            />
            <Route
              path="/administration"
              element={
                <ProtectedRoute cap="benutzer.verwalten">
                  <Administration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logs"
              element={
                <ProtectedRoute>
                  <SystemLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lieferanten"
              element={
                <ProtectedRoute>
                  <Lieferanten />
                </ProtectedRoute>
              }
            />
            <Route
              path="/abwiegen"
              element={
                <ProtectedRoute excludeRoles={["apotheke"]}>
                  <Abwiegen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute cap="zahlungen.sehen">
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zahlungsauftraege"
              element={
                <ProtectedRoute>
                  <KonfipayPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/telemediziner"
              element={
                <ProtectedRoute>
                  <Telemediziner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/produktemapping"
              element={
                <ProtectedRoute>
                  <Produktemapping />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/stockPreparation"
              element={
                <ProtectedRoute>
                  <StockPreparation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/filiale"
              element={
                <ProtectedRoute>
                  <BranchProductOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocols"
              element={
                <ProtectedRoute>
                  <ProtocolsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reset-print"
              element={
                <ProtectedRoute>
                  <ResetPrint />
                </ProtectedRoute>
              }
            />
            <Route
              path="/administration/scheduler"
              element={
                <ProtectedRoute roles={["admin", "manager"]}>
                  <SchedulerAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/administration/mollie-terminals"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <MollieTerminalsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/administration/cannaflow-shipping"
              element={<Navigate to="/administration/scheduler" replace />}
            />
            <Route
              path="/administration/warehouse-dashboard"
              element={
                <ProtectedRoute roles={["admin", "manager"]}>
                  <WarehouseDashboardAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ticketcenter"
              element={
                <ProtectedRoute>
                  <TicketCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistics-marketplace"
              element={
                <ProtectedRoute>
                  <MarketplaceStatistics />
                </ProtectedRoute>
              }
            />
            {import.meta.env.MODE === "development" && (
              <Route
                path="/dev/error-boundary-test"
                element={<DevErrorBoundaryTest />}
              />
            )}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

function App() {
  return (
    <ScaleProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <TerminalProvider>
              <AppContent />
              <ScaleModalGate />
              <TerminalModalGate />
              <ToastContainer
                position="top-right"
                autoClose={4000}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="light"
              />
            </TerminalProvider>
          </div>
        </Router>
      </AuthProvider>
    </ScaleProvider>
  );
}

export default App;
